import type React from "react";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

import { EncircledIcon } from "components/EncircledIcon";

import { GENERIC_ROUTES } from "constants/routes";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useRedirect } from "hooks/useRedirect";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge } from "styles/elements/Texts";
import { CssButton, CssFlex, CssLink } from "styles/helpers/layout";

import {
  type GroupEnrollmentOutcomeParams,
  type GroupEnrollmentOutcomeProps,
  GroupEnrollmentOutcomeType,
} from "./GroupEnrollmentOutcome.types";

import { StyledContentContainer, StyledGroupName } from "../../JoinGroupPage.styled";

// Renders a custom message based on whether the join was successful or not
export const GroupEnrollmentOutcome: React.FC<GroupEnrollmentOutcomeProps> = ({
  groupName,
  outcomeType,
}) => {
  const { t } = useTranslation("groups");
  const { redirectToUrl } = useRedirect();
  const { getCustomSupportEmailByDomain } = useDomainHandler();

  const outcomeParams = useMemo<GroupEnrollmentOutcomeParams>(() => {
    switch (outcomeType) {
      // Join failed because group was not found
      case GroupEnrollmentOutcomeType.NotExistingGroup:
        return {
          dataCy: "invalid-group-message",
          title: t("groups:joinGroup.outcome.invalidGroup.title"),
          description: t("groups:joinGroup.outcome.invalidGroup.description"),
          iconName: "fas fa-users",
          iconVariant: "error",
        };
      // Join failed because user lacks access permissions
      case GroupEnrollmentOutcomeType.NotAllowedToJoin:
        return {
          dataCy: "group-join-forbidden-message",
          title: t("groups:joinGroup.outcome.forbiddenJoin.title"),
          description: (
            <Trans
              components={{
                emailLink: <CssLink external to={`mailto:${getCustomSupportEmailByDomain()}`} />,
              }}
              i18nKey="groups:joinGroup.outcome.forbiddenJoin.description"
              t={t}
              values={{
                supportEmailAddress: getCustomSupportEmailByDomain(),
              }}
            />
          ),
          iconName: "fas fa-times",
          iconVariant: "error",
        };
      // Successful join
      default:
        return {
          dataCy: "group-join-success-message",
          title: t("groups:joinGroup.outcome.successfulJoin.title"),
          iconName: "fas fa-check",
          iconVariant: "success",
        };
    }
  }, [outcomeType]);

  return (
    <StyledContentContainer data-cy={outcomeParams.dataCy}>
      <CssFlex flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
        <EncircledIcon
          iconName={outcomeParams.iconName}
          variant={outcomeParams.iconVariant}
          size="large"
        />
        <Heading2>{outcomeParams.title}</Heading2>
        {groupName && <StyledGroupName data-cy="target-group-name">{groupName}</StyledGroupName>}
      </CssFlex>
      {outcomeParams.description && <ParagraphLarge>{outcomeParams.description}</ParagraphLarge>}
      <CssButton
        dataCy="return-home-button"
        variant="secondary"
        onClick={() => redirectToUrl(GENERIC_ROUTES.HOME_PAGE)}
      >
        {t("groups:joinGroup.buttonLabels.return")}
      </CssButton>
    </StyledContentContainer>
  );
};
